/**
 * 棋型识别模块
 * 用于识别各种棋型并计算分数
 */
export class PatternRecognizer {
    constructor() {
        // 方向定义
        this.directions = [
            [0, 1],   // 水平
            [1, 0],   // 垂直
            [1, 1],   // 主对角线
            [1, -1]   // 副对角线
        ];

        // 棋型权重 - 调整为更智能的策略
        this.scores = {
            five: 100000,        // 连五（必胜）
            open_four: 15000,    // 活四（进攻价值提升）
            double_four: 50000,   // 双四（极高价值）
            blocked_four: 800,    // 冲四（降低，避免无意义冲四）
            open_three: 2500,    // 活三（大幅提升，进攻核心）
            double_three: 12000,  // 双活三（极高价值，胜过活四）
            blocked_three: 200,   // 眠三
            open_two: 300,       // 活二（提升）
            blocked_two: 20,     // 眠二
            one: 5               // 单子（略微提升）
        };
    }

    /**
     * 计算某个方向上的棋型
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} player 玩家
     * @param {Array} direction 方向
     * @returns {Object} 棋型信息
     */
    countPattern(board, row, col, player, direction) {
        const [dr, dc] = direction;
        let count = 1;  // 当前位置
        let block = 0;   // 被阻挡端数
        let space = [];  // 空位列表

        // 向正方向搜索
        let r = row + dr, c = col + dc;
        while (r >= 0 && r < board.size && c >= 0 && c < board.size) {
            if (board.board[r][c] === player) {
                count++;
            } else if (board.board[r][c] === 0) {
                space.push({ row: r, col: c });
                break;
            } else {
                block++;
                break;
            }
            r += dr;
            c += dc;
        }

        // 向反方向搜索
        r = row - dr;
        c = col - dc;
        while (r >= 0 && r < board.size && c >= 0 && c < board.size) {
            if (board.board[r][c] === player) {
                count++;
            } else if (board.board[r][c] === 0) {
                space.push({ row: r, col: c });
                break;
            } else {
                block++;
                break;
            }
            r -= dr;
            c -= dc;
        }

        return { count, block, space };
    }

    /**
     * 评估一条线上的棋型
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} player 玩家
     * @param {Array} direction 方向
     * @returns {number} 分数
     */
    evaluateLine(board, row, col, player, direction) {
        const { count, block, space } = this.countPattern(board, row, col, player, direction);

        // 连五，必胜
        if (count >= 5) {
            return this.scores.five;
        }

        // 活四
        if (count === 4 && block === 0) {
            return this.scores.open_four;
        }

        // 冲四
        if (count === 4 && block === 1) {
            return this.scores.blocked_four;
        }

        // 活三
        if (count === 3 && block === 0) {
            if (space.length >= 2) {
                return this.scores.open_three;
            }
        }

        // 眠三
        if (count === 3 && block === 1) {
            return this.scores.blocked_three;
        }

        // 活二
        if (count === 2 && block === 0) {
            if (space.length >= 3) {
                return this.scores.open_two;
            }
        }

        // 眠二
        if (count === 2 && block === 1) {
            return this.scores.blocked_two;
        }

        // 单子
        return this.scores.one;
    }

    /**
     * 检测双活三
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} player 玩家
     * @returns {boolean} 是否形成双活三
     */
    hasDoubleOpenThree(board, row, col, player) {
        let openThreeCount = 0;

        // 临时放置棋子
        board.board[row][col] = player;

        for (const direction of this.directions) {
            const { count, block, space } = this.countPattern(board, row, col, player, direction);
            if (count === 3 && block === 0 && space.length >= 2) {
                openThreeCount++;
            }
        }

        // 撤销棋子
        board.board[row][col] = 0;

        return openThreeCount >= 2;
    }

    /**
     * 检测威胁（对手的活三或冲四）
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} opponent 对手
     * @returns {Object} 威胁信息
     */
    detectThreats(board, row, col, opponent) {
        let hasOpenFour = false;
        let hasOpenThree = false;
        let hasBlockedFour = false;

        // 临时放置对手棋子
        board.board[row][col] = opponent;

        for (const direction of this.directions) {
            const { count, block } = this.countPattern(board, row, col, opponent, direction);

            if (count === 4 && block === 0) {
                hasOpenFour = true;
            } else if (count === 4 && block === 1) {
                hasBlockedFour = true;
            } else if (count === 3 && block === 0) {
                hasOpenThree = true;
            }
        }

        // 撤销对手棋子
        board.board[row][col] = 0;

        return {
            hasOpenFour,
            hasOpenThree,
            hasBlockedFour,
            isUrgent: hasOpenFour || (hasBlockedFour && hasOpenThree)
        };
    }

    /**
     * 检测禁手（针对黑棋的三三禁手、四四禁手等）
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} player 玩家（只对黑棋生效）
     * @returns {boolean} 是否为禁手
     */
    isForbiddenMove(board, row, col, player) {
        // 只有黑棋有禁手规则
        if (player !== 1) return false;

        let doubleThreeCount = 0;
        let doubleFourCount = 0;

        // 临时放置棋子
        board.board[row][col] = player;

        // 检查双活三禁手
        for (const direction of this.directions) {
            const { count, block, space } = this.countPattern(board, row, col, player, direction);
            if (count === 3 && block === 0 && space.length >= 2) {
                doubleThreeCount++;
            }
            if (doubleThreeCount >= 2) break;
        }

        // 检查四四禁手
        for (const direction of this.directions) {
            const { count, block } = this.countPattern(board, row, col, player, direction);
            if (count >= 4) {
                doubleFourCount++;
            }
            if (doubleFourCount >= 2) break;
        }

        // 撤销棋子
        board.board[row][col] = 0;

        // 三三禁手和四四禁手
        return doubleThreeCount >= 2 || doubleFourCount >= 2;
    }

    /**
     * 评估某个玩家的整个棋盘
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {number} 总分
     */
    evaluatePosition(board, player) {
        let totalScore = 0;
        const evaluated = new Set(); // 避免重复评估

        for (let r = 0; r < board.size; r++) {
            for (let c = 0; c < board.size; c++) {
                if (board.board[r][c] === player) {
                    // 检查四个方向
                    for (const direction of this.directions) {
                        const key = `${r},${c},${direction.join(',')}`;
                        if (!evaluated.has(key)) {
                            const score = this.evaluateLine(board, r, c, player, direction);
                            totalScore += score;
                            evaluated.add(key);

                            // 标记这条线上其他点为已评估
                            const [dr, dc] = direction;
                            for (let i = -4; i <= 4; i++) {
                                const nr = r + i * dr;
                                const nc = c + i * dc;
                                if (nr >= 0 && nr < board.size && nc >= 0 && nc < board.size) {
                                    evaluated.add(`${nr},${nc},${direction.join(',')}`);
                                }
                            }
                        }
                    }
                }
            }
        }

        return totalScore;
    }

    /**
     * 快速评估某个位置的棋型
     * @param {Board} board 棋盘
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} player 玩家
     * @returns {number} 分数
     */
    quickEvaluatePosition(board, row, col, player) {
        let score = 0;

        // 临时放置棋子
        board.board[row][col] = player;

        // 评估该位置的棋型
        for (const direction of this.directions) {
            const lineScore = this.evaluateLine(board, row, col, player, direction);
            score += lineScore;
        }

        // 撤销棋子
        board.board[row][col] = 0;

        return score;
    }

    /**
     * 判断落子后是否会形成四
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @param {number} player 玩家
     * @returns {boolean} 是否会形成四
     */
    willFormFour(board, pos, player) {
        const { row, col } = pos;

        for (const direction of this.directions) {
            const { count, block } = this.countPattern(board, row, col, player, direction);

            if (count >= 4) {  // 会形成四
                return true;
            }
        }

        return false;
    }

    /**
     * 获取威胁类型
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @param {number} player 玩家
     * @returns {string|null} 威胁类型
     */
    getThreatType(board, pos, player) {
        const { row, col } = pos;

        for (const direction of this.directions) {
            const { count, block } = this.countPattern(board, row, col, player, direction);

            if (count >= 4) {
                return 'four';
            } else if (count === 3 && block === 0) {
                return 'open_three';
            }
        }

        return null;
    }
}